import { useEffect, useRef, useState, type JSX } from 'react';
import { Link, Navigate } from 'react-router';
import { usePostHog } from '@posthog/react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, StatusIndicator } from '@viniciomendesr/rayrovisk-design-system';
import { apiRequest } from '../api/client';
import { keys } from '../api/queries';
import type { CreatorProfile, Me } from '../api/types';
import { BrandHeader } from './PasswordScreenParts';

/** RS-5's budget. Past it, waiting is not the answer any more — a person is. */
const DEADLINE_MS = 15 * 60_000;
const POLL_MS = 5_000;

/**
 * Where the magic link lands. The Supabase SDK has already read the tokens out
 * of the fragment by the time this renders, so the only question left is
 * whether provisioning has caught up — which is what the timeline shows.
 */
export function AccessCallbackScreen(): JSX.Element {
  const [waitedOut, setWaitedOut] = useState(false);
  const accessGrantedFired = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => setWaitedOut(true), DEADLINE_MS);
    return () => clearTimeout(timer);
  }, []);

  const me = useQuery({
    queryKey: keys.me,
    queryFn: () => apiRequest<Me>('/v1/me'),
    refetchInterval: (query) => (hasAnyProduct(query.state.data) || waitedOut ? false : POLL_MS)
  });

  /**
   * Asked only once provisioning has landed, because before that there is no
   * product to open the route and the answer would be a 403 she never sees.
   */
  const profile = useQuery({
    queryKey: keys.profile,
    queryFn: () => apiRequest<{ profile: CreatorProfile | null }>('/v1/profile'),
    enabled: hasAnyProduct(me.data),
    retry: false
  });

  const posthog = usePostHog();

  const provisioned = hasAnyProduct(me.data) && !profile.isPending;
  /**
   * What she bought, not what she may read. Since 2026-08-06 the generator
   * opens the library too, so "can she read it" would send a generator buyer
   * to the catalogue instead of to the generator she paid for. The question
   * here is which product is hers.
   */
  const boughtTheLibrary =
    me.data?.products['prompt-pack'] === 'granted' ||
    me.data?.products['prompt-pack-starter'] === 'granted';
  const needsProfile = profile.isSuccess && profile.data.profile === null;
  const destination = needsProfile ? 'profile' : boughtTheLibrary ? 'biblioteca' : 'gerador';

  /**
   * The one event on this screen that is not a click: it marks the moment a
   * purchase turned into a working account, which is the join between what
   * Hotmart charged and what the product delivered.
   *
   * In an effect rather than in the render below because this render ends in a
   * redirect, and a capture written beside a `<Navigate>` is a side effect in a
   * render React is free to throw away. The ref keeps the polling query from
   * counting the same arrival twice.
   */
  useEffect(() => {
    if (!provisioned || accessGrantedFired.current) {
      return;
    }
    accessGrantedFired.current = true;
    posthog?.capture('access_granted', { destination });
  }, [provisioned, destination, posthog]);

  if (provisioned) {
    /**
     * **The first login goes to the profile**, decided 2026-08-06.
     *
     * It used to go straight to what she bought, on the reasoning that the first
     * thing after paying should be the thing she paid for and not a
     * questionnaire. The owner reversed it: the profile is what makes every
     * other screen produce her voice instead of a generic one, and a buyer who
     * never fills it never sees the product work. It is also what the script
     * trial needs — `POST /v1/scripts` refuses without one — so the ten free
     * roteiros are unreachable until this exists.
     *
     * **First, not compulsory.** `/gerador` renders inside the shell, so the
     * whole navigation is beside it and the library is one click away. A version
     * that trapped her behind six screens would be the questionnaire objection
     * being right.
     *
     * A profile that failed to load is not a reason to send her anywhere: she
     * goes to what she bought, which is what this screen did before.
     */
    if (profile.isSuccess && profile.data.profile === null) {
      return <Navigate to="/gerador" replace />;
    }

    return <Navigate to={boughtTheLibrary ? '/biblioteca' : '/gerador'} replace />;
  }

  return (
    <section
      style={{
        minHeight: '100vh',
        background: 'var(--rvk-color-cream-50)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(28px, 6vw, 56px) clamp(14px, 5vw, 24px)'
      }}
    >
      <div style={{ width: '100%', maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* She arrives here from her inbox with nothing else on screen to say
            whose product this is. Shared with the password screens so the two
            entry surfaces cannot drift apart. */}
        <BrandHeader />
        <Card layer="functional" elevation="raised" padding="lg">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <StatusIndicator
              status={waitedOut ? 'failed' : 'syncing'}
              pulse={!waitedOut}
              label={waitedOut ? 'Está demorando mais que o normal' : 'Liberando o seu acesso'}
            />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <h1 style={{ margin: 0, font: '400 27px/1.15 var(--rvk-font-expressive)' }}>
                Estamos preparando a sua biblioteca.
              </h1>
              <p
                style={{
                  margin: 0,
                  font: '400 14.5px/1.6 var(--rvk-font-functional)',
                  color: 'var(--rvk-text-secondary)'
                }}
              >
                Normalmente leva alguns minutos. Você não precisa ficar nesta tela: o link de entrada
                também chega por e-mail.
              </p>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                background: 'var(--rvk-surface-2)',
                border: '1px solid var(--rvk-border-subtle)',
                borderRadius: 'var(--rvk-radius-md)',
                padding: 6
              }}
            >
              <Step status="live" label="Entrada confirmada" detail="agora" />
              <Divider />
              <Step
                status={waitedOut ? 'failed' : 'syncing'}
                pulse={!waitedOut}
                label="Criando o seu acesso"
                detail={waitedOut ? 'passou de 15 minutos' : 'em andamento'}
              />
              <Divider />
              <Step status="idle" label="Biblioteca liberada" detail="aguardando" />
            </div>

            <p
              style={{
                margin: 0,
                font: '400 13.5px/1.6 var(--rvk-font-functional)',
                color: 'var(--rvk-text-secondary)'
              }}
            >
              Se passar de 15 minutos, fale com a gente: o acesso é liberado na mão, sem você ter que
              provar nada.
            </p>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Link to="/reenviar">
                <Button variant="secondary">Reenviar para outro e-mail</Button>
              </Link>
              <a href="mailto:acesso@roteirovisk.com">
                <Button variant="ghost">Falar com o suporte</Button>
              </a>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
}

function hasAnyProduct(me: Me | undefined): boolean {
  if (!me) {
    return false;
  }
  // `in_review` opens the product too — a refund request is not a refund
  // (RS-7), and the screen behind this one explains the state.
  return Object.values(me.products).some((state) => state === 'granted' || state === 'in_review');
}

function Divider(): JSX.Element {
  return <div style={{ height: 1, background: 'var(--rvk-border-subtle)' }} />;
}

function Step(props: {
  status: 'live' | 'idle' | 'syncing' | 'failed';
  label: string;
  detail: string;
  pulse?: boolean;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        padding: '12px 14px'
      }}
    >
      <StatusIndicator status={props.status} pulse={props.pulse} label={props.label} />
      <span style={{ font: '400 12px/1 var(--rvk-font-mono)', color: 'var(--rvk-text-tertiary)' }}>
        {props.detail}
      </span>
    </div>
  );
}
